import { useState } from "react";
import { RotateCw } from "lucide-react";

import { tx, useLang } from "../lib/i18n";
const DEFAULT_THEME = { bg: "#0B1112", bg2: "#1A2324", accent: "#00E5BC", text: "#F6F8F7" };

const Face = ({ theme, children, back }) => { useLang(); return (
  <div className={`mivo-card-face ${back ? "mivo-card-back" : ""}`}
    style={{ background: `radial-gradient(120% 140% at 85% 0%, ${theme.bg2} 0%, ${theme.bg} 60%)`, color: theme.text, borderColor: `${theme.accent}33` }}>
    <div className="mivo-card-sheen" style={{ background: `radial-gradient(60% 60% at 20% 110%, ${theme.accent}33, transparent 70%)` }} />
    <div className="relative flex h-full flex-col justify-between p-[6%]">{children}</div>
  </div>
); };

const TIER_THEMES = {
  essential: { bg: "#06110f", bg2: "#0d3b35", accent: "#2ee6c5", text: "#eefcf8", finish: "Midnight Teal" },
  silver: { bg: "#3b4148", bg2: "#c9ced4", accent: "#f4f6f8", text: "#101418", finish: "Brushed Titanium" },
  gold: { bg: "#5a4317", bg2: "#e3c477", accent: "#fff3cf", text: "#1b1406", finish: "Champagne Gold" },
  premier: { bg: "#070b1f", bg2: "#1d2c5e", accent: "#c7d2e8", text: "#eef2fb", finish: "Royal Midnight" },
  vip_black: { bg: "#050505", bg2: "#1c1c1c", accent: "#d9b56a", text: "#f2e6c9", finish: "Obsidian Elite" },
};

export const MivoCard = ({ tier, name, numberText, validText, issuedText, balanceText, codeText, back, sandbox = true, onFlip, testid = "mivo-card", children }) => { useLang();
  const theme = TIER_THEMES[tier?.code] || tier?.theme || DEFAULT_THEME;
  return (
    <div className={`mivo-card-wrap mivo-tier-${tier?.code || "default"}`} data-testid={testid}>
      <div className={`mivo-card-inner ${back ? "is-flipped" : ""} ${onFlip ? "cursor-pointer" : ""}`} role={onFlip ? "button" : undefined} tabIndex={onFlip ? 0 : undefined} aria-pressed={onFlip ? back : undefined}
        aria-label={onFlip ? tx(back ? "Show card front" : "Show card back") : undefined} data-testid={`${testid}-surface`}
        onClick={(e) => { if (onFlip && !e.target.closest("button,a,input")) onFlip(); }} onKeyDown={(e) => { if (onFlip && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onFlip(); } }}>
        <Face theme={theme}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <img src="/assets/brand/mivo-mark.png" alt="" className="h-6 w-auto sm:h-7" />
              <img src="/assets/brand/mivo-wordmark.png" alt="MIVO" className="h-3 w-auto sm:h-3.5" />
            </div>
            <span className="text-[9px] sm:text-[10px] font-semibold tracking-[0.18em]" style={{ color: theme.accent }} data-testid={`${testid}-balance`}>{tx(balanceText)}</span>
          </div>
          <div>
            <p className="text-[9px] sm:text-[10px] font-semibold tracking-[0.2em] opacity-80">{tx("MIVO SHOPPING CARD ·")} <span style={{ color: theme.accent }}>{(tier?.name || "").toUpperCase()}</span> {tx("· USDT PREPAID")}</p>
            <p className="mt-2 font-mono text-base sm:text-xl tracking-[0.12em]" data-testid={`${testid}-number`}>{tx(numberText)}</p>
          </div>
          <div className="flex items-end justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm sm:text-base font-semibold tracking-[0.06em]" data-testid={`${testid}-name`}>{tx(name || "YOUR NAME")}</p>
              <p className="mt-1 whitespace-nowrap text-[9px] sm:text-[10px] tracking-[0.15em] opacity-70">{issuedText && <>{tx("ISSUED")} <span data-testid={`${testid}-issued`}>{issuedText}</span> · </>}{tx("VALID THRU")} <span data-testid={`${testid}-valid`}>{tx(validText)}</span></p>
            </div>
            <p className="hidden shrink-0 text-right text-[8px] sm:block sm:text-[9px] leading-tight opacity-70">{tx(theme.finish || "")}<br />{tx("MIVO-only prepaid shopping card")}<br />{tx(sandbox && <span className="font-semibold text-amber-300">{tx("SANDBOX")}</span>)}</p>
          </div>
        </Face>
        <Face theme={theme} back>
          <div className="flex items-center justify-between">
            <img src="/assets/brand/mivo-wordmark.png" alt="MIVO" className="h-3 w-auto" />
            <span className="text-[9px] tracking-[0.18em] opacity-70">{tx("ADD FUNDS · CARD BACK")}</span>
          </div>
          <div className="flex-1 py-2">{children}</div>
          <div className="flex items-end justify-between">
            <p className="text-[9px] sm:text-[10px] tracking-[0.12em] opacity-80">{tx("MIVO CARD CODE (4 DIGITS):")} <span className="font-mono text-sm" data-testid={`${testid}-code`}>{tx(codeText)}</span></p>
            <p className="text-[8px] opacity-60">{tx("Not a bank CVV · Not your PIN")}</p>
          </div>
        </Face>
      </div>
      <p className="mt-2 text-[11px] text-[var(--mv-dim)]" data-testid={`${testid}-support`}>{tx("Problems with your MIVO card?")} <a href="/messages?topic=card" className="text-[var(--mv-teal)] hover:underline">{tx("Contact MIVO Customer Support")}</a></p>
      {tx(onFlip && (
        <button onClick={onFlip} className="mt-3 flex items-center gap-2 text-xs text-[var(--mv-muted)] hover:text-[var(--mv-teal)]" data-testid={`${testid}-flip`} aria-pressed={back}>
          <RotateCw className="h-3.5 w-3.5" /> {tx(back ? "Show front" : "Show back")}
        </button>
      ))}
    </div>
  );
};

export const useFlip = () => {
  const [back, setBack] = useState(false);
  return [back, () => setBack((b) => !b), setBack];
};
